/**
 * /experiments/new — one shell for every platform (`docs/agent-access-redesign.html`
 * §04): 1 name and description, 2 dataset, 3 runner (connection, then the
 * platform's screens for what to test and its inputs), 4 evaluators, 5 review
 * and test (save or start from here), 6 schedule (optional; coming soon).
 *
 * `?agent=<id>` starts from that agent (its page's "New experiment"), prefilled
 * from its last experiment; `?draft=<id>` edits a saved draft, whose target is
 * fixed but whose connection and version may change; `?from=<id>` duplicates
 * an experiment: every choice copied, the name numbered, saved as a new one.
 *
 * Unsaved choices are kept in the tab (`wizard-draft.ts`), so a reload or a
 * trip to upload a dataset comes back to them; `?dataset=<id>` is that trip's
 * return, and picks the new dataset on the dataset step.
 */

import { useEffect, useState } from "react";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { Crumbs, PageHeader } from "@/components/ui/page";
import { Skeleton } from "@/components/ui/states";
import { useAgent } from "@/features/agents/hooks";
import { useUrlState } from "@/lib/use-url-state";
import { useExperiment, useExperiments } from "../hooks";
import { copyName } from "./copy-name";
import { REVIEW, STEPS, reachable } from "./new-experiment-payload";
import { startBlocker } from "../run-package";
import { versionBlocker } from "./pulls/hooks";
import { StepDataset, StepEvaluators } from "./step-data";
import { StepName } from "./step-name";
import { StepSchedule } from "./step-schedule";
import { StepBar } from "./step-bar";
import { StepStart } from "./step-start";
import { stepValues } from "./step-values";
import { StepRunner } from "./step-target";
import { useNewExperiment, type Prefill } from "./use-new-experiment";
import { useSubmitExperiment } from "./use-submit-experiment";
import { clearDraft, draftKey, loadDraft, useKeepDraft, withDataset, withEvaluator } from "./wizard-draft";

/** Each step's heading and one line on what it decides. */
const INTRO: Array<[string, string]> = [
  ["Name the experiment", "Say what changed, so the experiments list reads like a changelog."],
  ["Pick the cases to run", "Each dataset item is one case: an input, and optionally the expected answer."],
  ["Choose who answers", "Run an agent on every case, or score the answers the dataset already holds."],
  ["Choose how to score", "Each evaluator adds a score per case and an average for the run."],
  ["Review and try one case", "Check what you picked, try a single case, then start."],
  ["Start the experiment", "It runs once, now. Running it on a schedule is coming soon."],
];

function Flow({
  prefill,
  storageKey,
  onReset,
}: {
  prefill: Prefill;
  storageKey: string;
  onReset: () => void;
}) {
  const url = useUrlState();
  const [saved] = useState(() =>
    withEvaluator(withDataset(loadDraft(storageKey), url.get("dataset")), url.get("evaluator")),
  );
  const flow = useNewExperiment(prefill, saved);
  const [picked, setStep] = useState(saved?.step ?? 0);
  const open = reachable(flow);
  const step = Math.min(picked, open);
  useKeepDraft(storageKey, { step: picked, ...flow.kept }, !saved);
  const [returned] = useState(() => !!url.get("dataset"));
  const [madeEvaluator] = useState(() => url.get("evaluator"));
  const { set } = url;
  useEffect(() => {
    if (returned) set({ dataset: null }, { replace: true });
  }, [returned, set]);
  useEffect(() => {
    if (madeEvaluator) set({ evaluator: null }, { replace: true });
  }, [madeEvaluator, set]);
  const { submit, busy } = useSubmitExperiment(flow, prefill.draft, () => clearDraft(storageKey));
  const { form } = flow;
  const blocker =
    (flow.runner === "agent"
      ? startBlocker(flow.target.spec.data, form.runSettings, form.credentialRefs)
      : null) ?? versionBlocker(flow.liveVersion);
  const ready = open === STEPS.length - 1 && !!form.name.trim() && !flow.limitError && !busy;
  // Scheduling is optional, so the experiment can be saved or started from the review on.
  // The last step starts it; from the review on it can also be saved as a draft.
  const last = step === STEPS.length - 1;
  const reviewed = step >= REVIEW;
  const values = stepValues(flow, open);
  const intro = INTRO[step] ?? ["", ""];

  return (
    <>
      {saved && (
        <p className="-mb-2 flex items-center gap-2 text-xs text-muted" role="status">
          {returned ? "Your new dataset is selected." : "Picked up where you left off."}
          <button type="button" className="link" onClick={() => (clearDraft(storageKey), onReset())}>
            Start over
          </button>
        </p>
      )}
      <StepBar
        label="Experiment steps"
        steps={STEPS.map((name, i) => ({ name, value: values[i] ?? "" }))}
        step={step}
        reachable={open}
        onPick={setStep}
      />
      <Box className="flex min-w-0 flex-col">
        <header className="flex flex-col gap-0.5 border-b border-line-soft px-6 py-4">
          <span className="text-[11px] font-semibold tracking-[0.05em] text-faint uppercase">
            Step {step + 1} of {STEPS.length}
          </span>
          <h2 className="text-base font-semibold">{intro[0]}</h2>
          <p className="text-xs text-muted">{intro[1]}</p>
        </header>
        <div className="flex flex-col gap-5 px-6 py-5">
          {step === 0 && <StepName flow={flow} />}
          {step === 1 && <StepDataset flow={flow} />}
          {step === 2 && <StepRunner flow={flow} locked={!!prefill.draft} />}
          {step === 3 && <StepEvaluators flow={flow} />}
          {step === 4 && <StepStart flow={flow} />}
          {step === 5 && <StepSchedule />}
        </div>
        <div className="flex items-center gap-3 border-t border-line-soft bg-well px-6 py-4">
          {step > 0 && (
            <Button variant="ghost" onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          {reviewed && blocker && (
            <span id="ne-blocker" className="text-xs text-err">
              {blocker}
            </span>
          )}
          <span className="flex-1" />
          {/* A live dataset pulls its period when the run starts: nothing to keep as a draft. */}
          {reviewed && flow.runner === "agent" && !flow.live && (
            <Button disabled={!ready} onClick={() => void submit(false)}>
              Save draft
            </Button>
          )}
          {last ? (
            <Button
              variant="primary"
              disabled={!ready || !!blocker}
              aria-describedby={blocker ? "ne-blocker" : undefined}
              onClick={() => void submit(true)}
            >
              Start experiment
            </Button>
          ) : (
            <Button variant="primary" disabled={step >= open} onClick={() => setStep(step + 1)}>
              Next
            </Button>
          )}
        </div>
      </Box>
    </>
  );
}

export function NewExperimentPage() {
  const url = useUrlState();
  const draft = useExperiment(url.get("draft") ?? "");
  const original = useExperiment(url.get("from") ?? "");
  const names = useExperiments({}, !!url.get("from"));
  const agentId = url.get("agent") ?? draft.data?.agent?.id ?? original.data?.agent?.id ?? "";
  const agent = useAgent(agentId);
  const loading =
    (!!url.get("draft") && draft.isPending) ||
    (!!url.get("from") && (original.isPending || names.isPending)) ||
    (!!agentId && agent.isPending);
  const editing = draft.data?.status === "draft" ? draft.data : undefined;
  const copy = original.data && {
    of: original.data,
    name: copyName(
      original.data.name,
      (names.data ?? []).map((e) => e.name),
    ),
  };
  const storageKey = draftKey({ agentId: agentId || undefined, draftId: editing?.id, copyOf: copy?.of.id });
  const [resets, setResets] = useState(0);

  return (
    <>
      <div className="flex flex-col gap-2.5">
        <Crumbs
          items={[
            { label: "Experiments", to: "/experiments" },
            { label: editing ? "Edit draft" : copy ? "Duplicate" : "New" },
          ]}
        />
        <PageHeader
          title={editing ? `Edit ${editing.name}` : copy ? `Duplicate ${copy.of.name}` : "New experiment"}
          subtitle="Name it, pick the dataset, the agent to run and the evaluators, then review and start."
        />
      </div>
      {loading ? (
        <Skeleton label="Loading the experiment" />
      ) : (
        <Flow
          key={`${storageKey}:${resets}`}
          storageKey={storageKey}
          prefill={{ agent: agent.data, draft: editing, copy }}
          onReset={() => setResets((n) => n + 1)}
        />
      )}
    </>
  );
}
